import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import Dexie, { type DBCore, type DBCoreMutateRequest, type Middleware } from 'dexie';
import { v4 as uuid } from 'uuid';
import { logger } from '../useSessionRecordingStore/lib/logger';
import { MemoryOnlyWriteError, isMemoryOnlyWriteError } from './memoryOnlyWriteError';

const AUDIO_WIPE_LOCK_NAME = 'heidi-audio-logout-wipe';

const abortError = (): Error => {
  const error = new Error('Audio wipe lock request aborted');
  error.name = 'AbortError';
  return error;
};

const waitForAbort = (signal: AbortSignal): Promise<never> =>
  new Promise((_, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    signal.addEventListener('abort', () => reject(abortError()), { once: true });
  });

/** Runs work under one browser-wide owner, with leader election as a fallback. */
export const withAudioWipeLock = async <T>(
  work: () => Promise<T>,
  signal?: AbortSignal
): Promise<T> => {
  if (signal?.aborted) throw abortError();

  if (typeof navigator !== 'undefined' && navigator.locks) {
    const options: LockOptions = { mode: 'exclusive' };
    if (signal) options.signal = signal;
    return navigator.locks.request(AUDIO_WIPE_LOCK_NAME, options, work);
  }

  const channel = new BroadcastChannel(AUDIO_WIPE_LOCK_NAME);
  const elector = createLeaderElection(channel);
  try {
    if (signal) {
      await Promise.race([elector.awaitLeadership(), waitForAbort(signal)]);
    } else {
      await elector.awaitLeadership();
    }
    if (signal?.aborted) throw abortError();
    return await work();
  } finally {
    await elector.die();
    await channel.close();
  }
};

// Avoid a dbV2 import cycle; these are stable persistence identifiers.
const V2_DB_NAME = 'HEIDI_AUDIO_LEDGER';
const LEGACY_DB_NAME = 'HEIDI_AUDIO';

const EPOCH_KEY = 'heidi-audio-write-epoch';
const WIPE_INCOMPLETE_KEY = 'heidi-audio-wipe-incomplete';
const HYDRATE_WIPE_WAIT_MS = 10_000;
const WIPE_MARKER_WARNING_INTERVAL_MS = 60_000;

let wipeMarkerWarning: {
  errorName: string;
  reportedAt: number;
  suppressedCount: number;
} | null = null;
let wipeMarkerRecovery: { reportedAt: number; suppressedCount: number } | null = null;
let wipeMarkerWriteFailed = false;

/** Raised when logout fencing invalidates a persistence write. */
export class WriteFenceStaleError extends Error {
  constructor(reason: string) {
    super(`audio write fence: ${reason}`);
    this.name = 'WriteFenceStaleError';
  }
}

export const isWriteFenceStaleError = (err: unknown): boolean =>
  err instanceof Error && err.name === 'WriteFenceStaleError';

// Re-exported, not re-declared: every importer and mock literal names this
// module, and the leaf exists only to keep `errors.ts` off the fence's imports.
export { MemoryOnlyWriteError, isMemoryOnlyWriteError };

let fenceClosed = false;
let storageListenerInstalled = false;
export type FenceCloseSource = 'local' | 'remote';
const fenceClosedSubscribers = new Set<(source: FenceCloseSource) => void>();
const fenceReopenedSubscribers = new Set<() => void>();

const readEpoch = (): number => {
  try {
    return Number(window.localStorage.getItem(EPOCH_KEY)) || 0;
  } catch {
    return 0;
  }
};

/** Captures the current write epoch before asynchronous work. */
export const captureEpoch = (): number => readEpoch();

/** Which stores the wipe could not clear. */
export type StoreSet = { v2: boolean; legacy: boolean };

const BOTH_STORES: StoreSet = { v2: true, legacy: true };

/**
 * `classified`: the wipe left stores uncleared and the marker still gates every
 * write. `armed`: a consumer that means to record under them opened writes,
 * durable or memory-only depending on which store failed. A `null` wipeId is an
 * arming with no marker behind it, so it suppresses nothing.
 */
type FenceRecovery =
  | { kind: 'clear' }
  | { kind: 'classified'; wipeId: string; failed: StoreSet }
  | { kind: 'armed'; wipeId: string | null; failed: StoreSet };

let recovery: FenceRecovery = { kind: 'clear' };

// Only the v2 store takes ledger writes, so only its audio can co-mingle with
// the previous user's. An uncleared store may still be writable, so only an
// explicit refusal keeps the recording out of that un-wiped audio.
const memoryOnlyWrites = (): boolean => recovery.kind === 'armed' && recovery.failed.v2;

/**
 * Whether ledger writes are already refused. Arming is idempotent, so this is
 * the transition test for anything that reports the switch to memory-only.
 */
export const isMemoryOnlyArmed = (): boolean => memoryOnlyWrites();

// The wipe id this load armed: its own marker must not block the writes the
// arming exists to open. Scoped to that id, so a sibling tab's fresh logout
// marker still gates.
const suppressedWipeId = (): string | null =>
  recovery.kind === 'armed' ? recovery.wipeId : null;

const clearFenceRecovery = (): void => {
  recovery = { kind: 'clear' };
};

const wipeMarkerBlocks = (): boolean => {
  const wipeId = getWipeIncompleteMarker();
  return wipeId !== null && wipeId !== suppressedWipeId();
};

/**
 * Returns an armed suppression to the gated state so the next prepare can
 * re-classify a healed store. Only a quiet caller may do it: under a live
 * memory-only recording the marker gates again and every write is refused.
 */
export const disarmUnusableStoreWrites = (): void => {
  if (recovery.kind !== 'armed') return;
  clearFenceRecovery();
};

/**
 * Rejects writes made under a closed or newer epoch, or under a wipe marker
 * this load did not arm — its own armed wipe id is deliberately let through.
 */
export const assertWriteAllowed = (capturedEpoch: number): void => {
  if (fenceClosed) throw new WriteFenceStaleError('fence closed (logout in progress)');
  if (capturedEpoch !== readEpoch()) throw new WriteFenceStaleError('stale epoch');
  // The marker closes the gap before cross-tab storage events arrive.
  if (wipeMarkerBlocks()) {
    throw new WriteFenceStaleError('wipe in progress');
  }
};

export const closeFence = (source: FenceCloseSource = 'local'): void => {
  // Logout ends whatever the suppression was protecting, so it never outlives
  // a close — otherwise the next load's prepare would keep the old arming.
  disarmUnusableStoreWrites();
  if (fenceClosed) return;
  fenceClosed = true;
  fenceClosedSubscribers.forEach((callback) => {
    try {
      callback(source);
    } catch {
      // Subscribers are best effort.
    }
  });
};

/** Reopens writes after hydrate confirms logout cleanup. */
export const reopenFence = (): void => {
  fenceClosed = false;
  fenceReopenedSubscribers.forEach((callback) => {
    try {
      callback();
    } catch {
      // Subscribers are best effort.
    }
  });
};

/** What the arming opened, so the caller can report both populations. */
export interface ArmedStoreWrites {
  memoryOnly: boolean;
  failed: StoreSet;
}

/**
 * Opens writes for a consumer that intends to record under a store the wipe
 * could not clear: memory-only when v2 failed, durable when only legacy did.
 * Returns null when there was nothing classified to arm.
 */
export const armUnusableStoreWrites = (): ArmedStoreWrites | null => {
  if (recovery.kind === 'clear') return null;
  const armed = {
    kind: 'armed' as const,
    wipeId: recovery.wipeId,
    failed: recovery.failed,
  };
  recovery = armed;
  reopenFence();
  return { memoryOnly: armed.failed.v2, failed: armed.failed };
};

/**
 * Opens memory-only writes for a start that tolerated a ledger storage failure;
 * without it every chunk spends a retry ladder against the failed store. No
 * marker is involved, so a foreign marker still gates.
 */
export const armMemoryOnlyWrites = (): ArmedStoreWrites => {
  const failed: StoreSet = {
    v2: true,
    legacy: recovery.kind === 'clear' ? false : recovery.failed.legacy,
  };
  recovery = {
    kind: 'armed',
    wipeId: recovery.kind === 'clear' ? null : recovery.wipeId,
    failed,
  };
  return { memoryOnly: true, failed };
};

export const isFenceClosed = (): boolean => fenceClosed;

export const subscribeFenceClosed = (
  callback: (source: FenceCloseSource) => void
): (() => void) => {
  fenceClosedSubscribers.add(callback);
  return () => fenceClosedSubscribers.delete(callback);
};

export const subscribeFenceReopened = (callback: () => void): (() => void) => {
  fenceReopenedSubscribers.add(callback);
  return () => fenceReopenedSubscribers.delete(callback);
};

/** Closes local writes and signals sibling tabs through localStorage. */
export const bumpEpoch = (): void => {
  try {
    window.localStorage.setItem(EPOCH_KEY, String(readEpoch() + 1));
  } catch {
    // localStorage is best effort; the in-memory fence still closes.
  }
  closeFence();
};

const errorDetails = (err: unknown) => ({
  error: err instanceof Error ? err.message : String(err),
});

/**
 * The marker names the stores still owing a wipe, so a cleared one is never
 * re-wiped under a recording started since. A bare wipe id, or anything
 * unrecognised, reads as both stores outstanding: under-wiping leaks PHI.
 */
const readWipeIncompleteMarker = (): { wipeId: string; failed: StoreSet } | null => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(WIPE_INCOMPLETE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      const scoped = parsed as { wipeId?: unknown; v2?: unknown; legacy?: unknown };
      if (typeof scoped.wipeId === 'string') {
        return {
          wipeId: scoped.wipeId,
          failed: { v2: scoped.v2 !== false, legacy: scoped.legacy !== false },
        };
      }
    }
  } catch {
    // Not JSON — the pre-scope format.
  }
  return { wipeId: raw, failed: { ...BOTH_STORES } };
};

// Random, not a clock: two wipes in the same millisecond would share an id, and
// the suppression is scoped by wipe-id equality. Returns whether the scope
// landed — a caller must not act on one the next load will not read back.
export const setWipeIncompleteMarker = (
  wipeId: string = uuid(),
  failed: StoreSet = BOTH_STORES
): boolean => {
  try {
    window.localStorage.setItem(
      WIPE_INCOMPLETE_KEY,
      JSON.stringify({ wipeId, v2: failed.v2, legacy: failed.legacy })
    );
  } catch (err) {
    wipeMarkerWriteFailed = true;
    const errorName = err instanceof Error ? err.name : typeof err;
    const now = Date.now();
    if (
      wipeMarkerWarning?.errorName === errorName &&
      now >= wipeMarkerWarning.reportedAt &&
      now - wipeMarkerWarning.reportedAt < WIPE_MARKER_WARNING_INTERVAL_MS
    ) {
      wipeMarkerWarning.suppressedCount += 1;
      return false;
    }
    const suppressedCount = wipeMarkerWarning?.suppressedCount ?? 0;
    const suppressedErrorName = wipeMarkerWarning?.errorName;
    wipeMarkerWarning = { errorName, reportedAt: now, suppressedCount: 0 };
    logger.warn({
      event_type: 'WIPE_MARKER_WRITE_FAILED',
      details: {
        wipeId,
        errorName,
        suppressedCount,
        ...(suppressedCount > 0 ? { suppressedErrorName } : {}),
      },
    });
    return false;
  }
  if (!wipeMarkerWriteFailed) return true;
  wipeMarkerWriteFailed = false;
  const now = Date.now();
  if (
    wipeMarkerRecovery &&
    now >= wipeMarkerRecovery.reportedAt &&
    now - wipeMarkerRecovery.reportedAt < WIPE_MARKER_WARNING_INTERVAL_MS
  ) {
    wipeMarkerRecovery.suppressedCount += 1;
    return true;
  }
  const suppressedRecoveries = wipeMarkerRecovery?.suppressedCount ?? 0;
  wipeMarkerRecovery = { reportedAt: now, suppressedCount: 0 };
  const suppressedCount = wipeMarkerWarning?.suppressedCount ?? 0;
  const suppressedErrorName = wipeMarkerWarning?.errorName;
  if (wipeMarkerWarning) wipeMarkerWarning.suppressedCount = 0;
  logger.info({
    event_type: 'WIPE_MARKER_WRITE_RECOVERED',
    details: {
      wipeId,
      suppressedCount,
      ...(suppressedCount > 0 ? { suppressedErrorName } : {}),
      ...(suppressedRecoveries > 0 ? { suppressedRecoveries } : {}),
    },
  });
  return true;
};

export const getWipeIncompleteMarker = (): string | null =>
  readWipeIncompleteMarker()?.wipeId ?? null;

export const clearWipeIncompleteMarker = (expectedWipeId?: string): void => {
  try {
    if (expectedWipeId !== undefined && getWipeIncompleteMarker() !== expectedWipeId) {
      return;
    }
    window.localStorage.removeItem(WIPE_INCOMPLETE_KEY);
  } catch {}
};

export const hasWipeIncompleteMarker = (): boolean => {
  return getWipeIncompleteMarker() !== null;
};

/** Which generation of audio store a fenced connection belongs to. */
export type FencedStore = 'v2' | 'legacy';

/** Removes rows rather than landing new ones — see the memory-only gate below. */
const removesData = (type: DBCoreMutateRequest['type']): boolean =>
  type === 'delete' || type === 'deleteRange';

/** Rejects mutations while closed; transaction middleware must not defer them. */
const fenceMiddleware = (store: FencedStore): Middleware<DBCore> => ({
  stack: 'dbcore',
  name: 'audioWriteFence',
  create: (down) => ({
    ...down,
    table: (name) => {
      const table = down.table(name);
      return {
        ...table,
        mutate: (req) => {
          // Wipe connections use a separate, unfenced Dexie instance.
          if (fenceClosed || wipeMarkerBlocks())
            return Promise.reject(new WriteFenceStaleError('fence closed'));
          // The choke point that makes memory-only real: only v2 writes that land
          // data are refused. A delete shrinks the store the mode exists to stop
          // filling, so cleanup keeps reclaiming quota.
          if (store === 'v2' && memoryOnlyWrites() && !removesData(req.type))
            return Promise.reject(new MemoryOnlyWriteError());
          return table.mutate(req);
        },
      };
    },
  }),
});

const fencedDatabases = new Set<Dexie>();

/** Attaches before open so Dexie includes the middleware in its DBCore stack. */
export const attachWriteFence = (db: Dexie, store: FencedStore): void => {
  db.use(fenceMiddleware(store));
  fencedDatabases.add(db);
};

/**
 * Closes this tab's fenced connections before a wipe or delete: an open handle
 * can block `deleteDatabase` indefinitely. `disableAutoOpen: false` restores
 * each connection on its next use.
 */
export const closeFencedDatabasesForWipe = (): void => {
  fencedDatabases.forEach((attached) => {
    try {
      attached.close({ disableAutoOpen: false });
    } catch {
      // Best effort — the wipe/delete that follows is the real work.
    }
  });
};

/** Closes this tab's fence when another tab bumps the epoch. */
export const installWriteFence = (): void => {
  if (storageListenerInstalled || typeof window === 'undefined') return;
  storageListenerInstalled = true;
  window.addEventListener('storage', (e) => {
    if (e.key === EPOCH_KEY) closeFence('remote');
  });
};

/** Clears an existing database through a dedicated unfenced connection. */
export const wipeUnfenced = async <T>(
  dbName: string,
  collect?: (raw: Dexie) => Promise<T>
): Promise<{ existed: boolean; stats?: T }> => {
  if (!(await Dexie.exists(dbName))) return { existed: false };
  const raw = new Dexie(dbName);
  try {
    await raw.open();
    let stats: T | undefined;
    await raw.transaction('rw', raw.tables, async () => {
      if (collect) stats = await collect(raw);
      await Promise.all(raw.tables.map((t) => t.clear()));
    });
    return { existed: true, stats };
  } finally {
    raw.close();
  }
};

/** Wipes one database, deleting it outright when it cannot be opened. */
const rewipeOrDelete = async (dbName: string): Promise<boolean> => {
  try {
    await wipeUnfenced(dbName);
    return true;
  } catch (err) {
    logger.warn({
      event_type: 'HYDRATE_REWIPE_FALLBACK_DELETE',
      details: { db: dbName, ...errorDetails(err) },
    });
    try {
      await Dexie.delete(dbName);
      return true;
    } catch (deleteErr) {
      logger.error({
        event_type: 'HYDRATE_REWIPE_FAILED',
        details: { db: dbName, ...errorDetails(deleteErr) },
      });
      return false;
    }
  }
};

/**
 * `ready`: no wipe outstanding or it completed. `wipe-pending`: cleanup may
 * yet succeed — wait. `store-unusable`: wipe AND delete both failed, and the
 * marker is kept so a healed store is still wiped later. Nothing is armed until
 * a consumer that means to record under it calls `armUnusableStoreWrites`.
 */
export type FenceRecoveryOutcome = 'ready' | 'wipe-pending' | 'store-unusable';

/** Confirms prior logout cleanup before reopening writes for hydration. */
type HydrateWipeLock = (
  work: () => Promise<FenceRecoveryOutcome>,
  signal?: AbortSignal
) => Promise<FenceRecoveryOutcome>;

export const prepareWriteFenceForHydrate = async (
  runWithLock: HydrateWipeLock = withAudioWipeLock
): Promise<FenceRecoveryOutcome> => {
  installWriteFence();
  // Sticky: prepare runs on every session change and every start, and a live
  // memory-only recording survives neither re-gating nor a re-wipe. Only a
  // quiet start disarms, which is where a healed store regains durability.
  if (recovery.kind === 'armed') return 'store-unusable';
  clearFenceRecovery();
  if (!hasWipeIncompleteMarker()) {
    reopenFence();
    return 'ready';
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HYDRATE_WIPE_WAIT_MS);
  try {
    return await runWithLock(async () => {
      const marker = readWipeIncompleteMarker();
      if (!marker) {
        reopenFence();
        return 'ready';
      }
      const { wipeId } = marker;

      closeFencedDatabasesForWipe();
      // Sequential on purpose (concurrent IDB deletes contend); a failure on
      // one store must still attempt the other. Only the stores the marker
      // still owes are touched.
      const v2Cleared = marker.failed.v2 ? await rewipeOrDelete(V2_DB_NAME) : true;
      const legacyCleared = marker.failed.legacy
        ? await rewipeOrDelete(LEGACY_DB_NAME)
        : true;
      if (!v2Cleared || !legacyCleared) {
        const failed = { v2: !v2Cleared, legacy: !legacyCleared };
        // Shrink the scope so the store that cleared is never wiped again —
        // by then it may hold a recording this load started.
        const narrowed = setWipeIncompleteMarker(wipeId, failed);
        logger.error({
          event_type: 'HYDRATE_WIPE_STORE_UNUSABLE',
          details: { v2Cleared, legacyCleared, markerNarrowed: narrowed },
        });
        // The persisted marker decides what the next load re-wipes, so this load
        // may never owe less than it does — a failed narrowing means adopting the
        // wider scope still on disk.
        const owed = narrowed
          ? failed
          : (readWipeIncompleteMarker()?.failed ?? { ...BOTH_STORES });
        // Classify only — `armUnusableStoreWrites` is the consumer's act.
        recovery = { kind: 'classified', wipeId, failed: owed };
        return 'store-unusable';
      }
      clearWipeIncompleteMarker(wipeId);
      if (hasWipeIncompleteMarker()) {
        closeFence();
        return 'wipe-pending';
      }

      reopenFence();
      return 'ready';
    }, controller.signal);
  } catch (err) {
    if (controller.signal.aborted) {
      logger.error({ event_type: 'HYDRATE_WIPE_WAIT_TIMEOUT' });
      closeFence();
      return 'wipe-pending';
    }
    logger.error({
      event_type: 'HYDRATE_REWIPE_FAILED',
      details: errorDetails(err),
    });
    closeFence();
    return 'wipe-pending';
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Test-only. Resets fence state, never the subscriber sets: modules subscribe
 * once at import, so clearing them silently unwires production for the rest of
 * the file. Test-registered subscribers unsubscribe with their own handle.
 */
export const __resetWriteFenceForTests = (): void => {
  fenceClosed = false;
  wipeMarkerWarning = null;
  wipeMarkerRecovery = null;
  wipeMarkerWriteFailed = false;
  clearFenceRecovery();
  storageListenerInstalled = false;
  try {
    window.localStorage.removeItem(EPOCH_KEY);
    window.localStorage.removeItem(WIPE_INCOMPLETE_KEY);
  } catch {}
};
